import { useEffect } from 'react';
import { CheckCircle, ArrowRight, Calendar, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Link } from 'react-router-dom';

export default function ThankYouPage() {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 pt-24 lg:pt-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Thank You for Your Interest!
          </h1>
          
          <p className="text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Your consultation request has been successfully submitted. Our Zoho experts will review your requirements 
            and get back to you within 24 hours with a customized solution plan.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* What Happens Next */}
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-gray-900 flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-blue-600" />
                What Happens Next?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-blue-600">1</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Expert Review</h3>
                    <p className="text-sm text-gray-600">Our Zoho certified consultants will analyze your business requirements</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-blue-600">2</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Personalized Plan</h3>
                    <p className="text-sm text-gray-600">We'll create a custom implementation roadmap for your business</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-blue-600">3</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Free Consultation</h3>
                    <p className="text-sm text-gray-600">Schedule a detailed discussion about your Zoho transformation</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-gray-900 flex items-center">
                <MessageCircle className="h-5 w-5 mr-2 text-green-600" />
                Need Immediate Help?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Contact Our Experts</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Don't wait! Our team is available to answer your questions right away.
                  </p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <MessageCircle className="h-4 w-4 mr-2 text-blue-600" />
                      <span className="font-medium">WhatsApp: +91 8447103081</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                      <span>Available 9 AM - 7 PM IST</span>
                    </div>
                  </div>
                </div>
                
                <a href="https://wa.me/918447103081" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-green-600 hover:bg-green-700">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Chat Now for Instant Support
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 lg:p-8 shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
            While You Wait, Explore More About Zoho
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to="/services">
              <Button variant="outline" className="w-full h-auto p-4 flex flex-col items-center space-y-2 hover:bg-blue-50">
                <CheckCircle className="h-6 w-6 text-blue-600" />
                <span className="font-medium">Our Services</span>
                <span className="text-xs text-gray-600 text-center">Discover our Zoho implementation expertise</span>
              </Button>
            </Link>
            
            <Link to="/partners">
              <Button variant="outline" className="w-full h-auto p-4 flex flex-col items-center space-y-2 hover:bg-purple-50">
                <ArrowRight className="h-6 w-6 text-purple-600" />
                <span className="font-medium">Integrations</span>
                <span className="text-xs text-gray-600 text-center">See how we connect Zoho with other tools</span>
              </Button>
            </Link>
            
            <Link to="/about">
              <Button variant="outline" className="w-full h-auto p-4 flex flex-col items-center space-y-2 hover:bg-green-50">
                <Calendar className="h-6 w-6 text-green-600" />
                <span className="font-medium">About Us</span>
                <span className="text-xs text-gray-600 text-center">Learn about our team and experience</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Success Message */}
        <div className="text-center mt-12 p-6 bg-green-50 rounded-xl border border-green-200">
          <h3 className="text-lg font-medium text-green-800 mb-2">
            🎉 Your Journey to Business Transformation Starts Now!
          </h3>
          <p className="text-green-700">
            Join 500+ satisfied clients who have revolutionized their business operations with our Zoho solutions.
          </p>
        </div>
      </div>
    </div>
  );
}